import axios from "axios";

const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
export const API = `${backendBase}/api`;

const TOKEN_KEY = "pinaki_crm_token";
const USER_KEY = "pinaki_crm_user";

// Helper to extract cookie values for CSRF protection
const getCookie = (name) => {
  try {
    const match = document.cookie.match(new RegExp("(^|;\\s*)(" + name + ")=([^;]*)"));
    return match ? decodeURIComponent(match[3]) : null;
  } catch {
    return null;
  }
};

export const getStoredToken = () => {
  // SEC-05: Deprecated and removed. Access tokens are strictly stored in httpOnly cookies.
  return null;
};

export const getStoredUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

let inMemoryCsrfToken = null;

export const setAuthSession = (token, user) => {
  try {
    // SEC-05: Clean up any legacy localStorage token; only cache non-sensitive user profile metadata
    localStorage.removeItem(TOKEN_KEY);
    if (user?.csrf_token) {
      inMemoryCsrfToken = user.csrf_token;
    }
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  } catch {}
};

export const clearAuthSession = () => {
  try {
    inMemoryCsrfToken = null;
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
  } catch {}
};

// Configured Axios client with credentials and interceptors
const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Request Interceptor: Attach CSRF header on state-changing requests (SEC-11)
apiClient.interceptors.request.use((config) => {
  const method = (config.method || "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(method)) {
    const csrfToken = getCookie("csrf_token") || inMemoryCsrfToken;
    if (csrfToken && !config.headers["X-CSRF-Token"]) {
      config.headers["X-CSRF-Token"] = csrfToken;
    }
  }
  return config;
});

// Response Interceptor: Sliding session refresh on 401 using httpOnly cookies
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const url = originalRequest?.url || "";

    const isAuthEndpoint =
      url.includes("/auth/login") ||
      url.includes("/auth/refresh") ||
      url.includes("/auth/logout");

    if (error.response?.status === 401 && !originalRequest?._retry && !isAuthEndpoint) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => apiClient(originalRequest))
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshRes = await axios.post(
          `${API}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        const userProfile = refreshRes.data;
        if (userProfile) {
          setAuthSession(null, userProfile);
          processQueue(null, null);
        } else {
          processQueue(null, null);
        }

        return apiClient(originalRequest);
      } catch (refreshErr) {
        processQueue(refreshErr, null);
        clearAuthSession();
        return Promise.reject(refreshErr);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export const api = (method, path, data, opts = {}) => {
  const isGet = (method || "").toLowerCase() === "get";
  if (isGet && data && !opts.params && data.params) {
    return apiClient({ method, url: path, ...data, ...opts });
  }
  return apiClient({ method, url: path, data, ...opts });
};

export const uploadFile = (orderId, file, category = "General") => {
  const form = new FormData();
  form.append("file", file);
  return apiClient.post(`/orders/${orderId}/documents?category=${encodeURIComponent(category)}`, form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const downloadDocument = (documentId, inline = false) => {
  return `${API}/documents/${documentId}/download${inline ? "?inline=1" : ""}`;
};

